import React, { useState } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { SingleDatePicker } from '../../../../../../../../CommonComponents/OPL/dates/SingleDatePicker/SingleDatePicker';
import { BeneficiaryType } from '../../../../../../../../ProfileOPL/PersonalInfo/api/useTrustsQuery';
import { BeneficiariesTable } from '../BeneficiariesTable/BeneficiariesTable';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { useAccountFormContext } from '../../context/useAccountFormContext';
import { FormFields } from '../../AccountForm.meta';
import { useAccountOwnership } from '../AccountOwnership/useAccountOwnership';

export const Beneficiaries = () => {
  const { control } = useFormContext<FormFields>();

  const watchAccountBeneficiaries = useWatch({
    name: 'bankAccount.AccountBeneficiaries',
    control,
  });

  const watchOwnerModelSelection = useWatch({ name: 'bankAccount.OwnerModel', control });

  const { options: accountOwnerOptions } = useAccountOwnership();

  const hasTrustOptions = accountOwnerOptions.find(val => val.label === 'Trusts');

  const isReadOnly = !!(watchOwnerModelSelection.length === 1 && hasTrustOptions
    ? hasTrustOptions?.options.find(
        option =>
          option.value === watchOwnerModelSelection[0].Id &&
          option.label === watchOwnerModelSelection[0].Name,
      )
    : false);

  const { fields, append, remove } = useFieldArray<
    FormFields,
    'bankAccount.AccountBeneficiaries.Beneficiaries',
    'fieldKey'
  >({
    control,
    name: 'bankAccount.AccountBeneficiaries.Beneficiaries',
    keyName: 'fieldKey',
  });

  const { options } = useAccountFormContext();

  const [beneficiariesOptions, setBeneficiariesOptions] = useState<
    { label: string; value: string | null; isVisible: boolean }[]
  >(
    options.BeneficiariesOptions.map(b => ({
      label: b.Name,
      value: b.Id,
      isVisible: !watchAccountBeneficiaries?.Beneficiaries?.find(
        beneficiary => beneficiary.Id === b.Id,
      ),
    })),
  );

  const beneficiaryFields = fields.map((field, index) => ({
    ...field,
    ...watchAccountBeneficiaries?.Beneficiaries?.[index],
  }));

  return (
    <>
      <BeneficiariesTable
        type={BeneficiaryType.Primary}
        width="auto"
        mx={-24}
        my="24"
        beneficiaryFields={beneficiaryFields}
        onAppend={append}
        onRemove={remove}
        options={beneficiariesOptions}
        onUpdateOptions={setBeneficiariesOptions}
        isReadOnly={isReadOnly}
      />

      <BeneficiariesTable
        type={BeneficiaryType.Contingent}
        width="auto"
        mx={-24}
        beneficiaryFields={beneficiaryFields}
        onAppend={append}
        onRemove={remove}
        options={beneficiariesOptions}
        onUpdateOptions={setBeneficiariesOptions}
        isReadOnly={isReadOnly}
      />

      {!isReadOnly && (
        <>
          <Text as="p" variant="formLabel" css="display: block;" mt="24" mb="4">
            When were these instructions last updated?
          </Text>

          <Controller
            control={control}
            name="bankAccount.AccountBeneficiaries.LastBeneficiaryUpdate"
            render={({ field: { value, onChange } }) => (
              <SingleDatePicker
                date={value === null ? new Date() : new Date(value)}
                onChange={onChange}
                maxDate={new Date()}
                minDate={new Date(1900, 0)}
                disabled={isReadOnly}
              />
            )}
          />
        </>
      )}

      <Text fontSize="small" color="neutral.500" mt="24">
        This is for financial planning informational purposes only. Any information entered here
        will not be updated at your financial institution. To update beneficiary information with
        your financial institution, please contact your advisor.
      </Text>
    </>
  );
};
